import {
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import request from "../../../api/request";
import "./UploadPreviewTable.css";
import "./UploadOrder.css";
import {
  getSimpleMaterials,
  QueryOptionsItem,
} from "../../../component/FilterPanel/FilterPanel";
import { AuthContext } from "../../../auth/authContext";
import { BackendAPI } from "../../../const/api";
import EditModal, { EditField } from "../../../component/EditModal/EditModal";
import { useToast } from "../../../component/ToastBar";
import humps from "humps";

interface OrderPreviewCustomer {
  id: string;
  name: string;
  code?: string;
}

interface OrderPreviewMaterial {
  id: string;
  name: string;
  model: string;
}

interface PriceListEntry {
  name: string;
  code: string;
  customerCode?: string;
}

interface OrderPreviewData {
  customers: OrderPreviewCustomer[];
  materials: OrderPreviewMaterial[];
  externalId: string;
  customerId: string;
  customerName: string;
  customerCode: string;
  salesperson: string;
  materialId: string;
  materialName: string;
  materialModel: string;
  quantity: number;
  unitPrice: number;
  priceListCode: string;
  priceListName: string;
  billingName: string;
  billingAddress: string;
  billingTel: string;
  note: string;
  createdAt: string;
  errors: string[];
  warnings: string[];
  index?: number;
}

interface PriceList {
  customer?: PriceListEntry[];
  default?: PriceListEntry[];
  common?: PriceListEntry[];
  [key: string]: PriceListEntry[] | undefined;
}

const defaultOrderPreview = {
  customers: [],
  materials: [],
  externalId: "",
  customerId: "",
  customerName: "",
  customerCode: "",
  salesperson: "",
  materialId: "",
  materialName: "",
  materialModel: "",
  quantity: 0,
  unitPrice: 0,
  priceListCode: "",
  priceListName: "",
  billingName: "",
  billingAddress: "",
  billingTel: "",
  note: "",
  createdAt: "",
  errors: [],
  warnings: [],
  index: 0,
};

export const ORDER_LINE_ERROR_MESSAGES: Record<string, string> = {
  INVALID_MATERIAL_NAME: "物料名称/型号不存在",
  INVALID_CUSTOMER_NAME: "缺失客户名称",
  CUSTOMER_NAME_NOT_FOUND: "客户名不存在",
  QUANTITY_NOT_FOUND: "缺失数量",
  QUANTITY_NOT_VALID: "数量不合法",
  UNIT_PRICE_NOT_FOUND: "缺失单价",
  UNIT_PRICE_NOT_VALID: "单价格式不合法",
  UNIT_PRICE_BELOW_FLOOR: "单价低于限价",
  PRICE_LIST_NOT_FOUND: "未选择或不可用限价表",
  BILLING_INFO_NOT_FOUND: "收货信息不完整",
  ORDER_ID_NOT_FOUND: "缺失订单id",
  CARRIER_NOT_FOUND: "缺失物流商",
  TRACKING_NUMBER_NOT_FOUND: "缺失物流单号",
  SHIPPING_ADDRESS_NOT_FOUND: "缺失发货地址",
  ORDER_NOT_SHIPPABLE: "订单非可发货状态",
  MULTIPLE_MATERIALS_MATCHED: "存在多个匹配的物料",
  MULTIPLE_CUSTOMERS_MATCHED: "存在多个匹配的客户",
  DUPLICATE_ORDER: "可能存在重复订单",
  UNIT_PRICE_MATCHED: "单价已按历史订单或默认价匹配，请核对",
};

export const formatOrderLineError = (code: string) =>
  ORDER_LINE_ERROR_MESSAGES[code] ?? code;

const normalizeOrderPreviewRows = (
  orders: OrderPreviewData[],
  priceList: PriceList | null,
  isCustomer: boolean,
) => {
  if (!priceList) {
    return orders;
  }
  const customerPriceList = priceList.customer || [];
  for (const p of customerPriceList) {
    const bucketKey = p.customerCode;
    if (!bucketKey) {
      continue;
    }
    if (!priceList[bucketKey]) {
      priceList[bucketKey] = [];
    }
    priceList[bucketKey]!.push(p);
  }
  orders.forEach((p) => {
    if (!isCustomer && p.customers?.length > 0) {
      p.customerId = p.customers[0].id;
      p.customerName = p.customers[0].name;
      p.customerCode = p.customers[0].code || "";
    }
    if (p.materials?.length > 0) {
      p.materialId = p.materials[0].id;
      p.materialName = p.materials[0].name;
      p.materialModel = p.materials[0].model;
    }
    p.errors = (p.errors || []).map((e) => String(e));
    p.warnings = (p.warnings || []).map((e) => String(e));
    if (!p.priceListCode) {
      const customerPriceLists = p.customerCode
        ? priceList[p.customerCode]
        : null;
      const defaultPriceList = priceList["default"]?.[0];
      const selectedPriceList =
        customerPriceLists && customerPriceLists.length > 0
          ? customerPriceLists[0]
          : defaultPriceList;
      if (selectedPriceList) {
        p.priceListCode = selectedPriceList.code;
        p.priceListName = selectedPriceList.name;
      }
    } else {
      p.priceListName =
        [
          ...(priceList["customer"] || []),
          ...(priceList["default"] || []),
          ...(priceList["common"] || []),
        ].find((pl) => pl.code === p.priceListCode)?.name || "";
    }
    if ((!p.priceListName || p.priceListName.length === 0) && priceList["default"]?.[0]) {
      p.priceListCode = priceList["default"][0].code;
      p.priceListName = priceList["default"][0].name;
    }
  });
  return orders;
};

export const getCustomerPriceListOptions = (
  customerCode: string,
  priceList: PriceList | null,
): QueryOptionsItem[] => {
  if (!priceList) {
    return [];
  }
  const defaultOptions = priceList["default"];
  const commonOptions = priceList["common"];
  const customerOptions = priceList["customer"];
  const currentCustomerOptions = customerOptions?.filter(
    (p) => p.customerCode === customerCode,
  );
  return [
    ...(currentCustomerOptions || []),
    ...(defaultOptions || []),
    ...(commonOptions || []),
  ].map((p) => ({
    value: p.code,
    label: p.name,
    data: p,
  }));
};

const UploadOrder = () => {
  const toast = useToast();
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<OrderPreviewData[]>([]);
  const [priceList, setPriceList] = useState<PriceList | null>(null);
  const [editData, setEditData] = useState<OrderPreviewData>({
    ...defaultOrderPreview,
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const navigator = useNavigate();
  const allowedExtensions = [".csv", ".xlsx", ".xls"]; // 允许的文件扩展名
  const authContext = useContext(AuthContext);
  const [modelOptions, setModelOptions] = useState<QueryOptionsItem[]>([]);
  const [customerOptions, setCustomerOptions] = useState<QueryOptionsItem[]>(
    [],
  );
  const fetchDataRef = useRef(false);
  const previewSectionRef = useRef<HTMLDivElement>(null);
  const [uploading, setUploading] = useState(false);
  const [needKdCheck, setNeedKdCheck] = useState<boolean>(true);
  const [customerNameInput, setCustomerNameInput] = useState<string>("");

  const matchCustomer = useCallback(async (customerNameInput: string) => {
    const res = await request.get(BackendAPI.matchCustomer, {
      query: customerNameInput,
      brief: true,
    });
    if (res.success && res.data.length > 0) {
      return res.data[0];
    } else {
      return;
    }
  }, []);

  useEffect(() => {
    if (authContext && !authContext.isCustomer()) {
      matchCustomer(customerNameInput).then((customer) => {
        if (customer) {
          setEditData({
            ...editData,
            customerId: customer.id,
            customerName: customer.name,
            customerCode: customer.code,
          });
        }
      });
    }
    // eslint-disable-next-line
  }, [customerNameInput, matchCustomer]);

  const fetchData = useCallback(async () => {
    const materials: QueryOptionsItem[] = await getSimpleMaterials();
    setModelOptions(materials);
    if (authContext && authContext.isAdmin()) {
      const res = await request.get(BackendAPI.customerAll);
      if (res.success) {
        setCustomerOptions(
          res.data.map(
            (c) =>
              ({
                value: c.id,
                label: c.name,
                data: c,
              }) as QueryOptionsItem,
          ),
        );
      }
    }
  }, [authContext]);

  useEffect(() => {
    if (!fetchDataRef.current) {
      fetchDataRef.current = true;
      fetchData().then();
    }
  }, [fetchData]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.slice(
        ((selectedFile.name.lastIndexOf(".") - 1) >>> 0) + 2,
      );
      if (allowedExtensions.includes(`.${fileExtension.toLowerCase()}`)) {
        setFile(selectedFile);
        setSelectedFileName(selectedFile.name); // 设置已选择的文件名
      } else {
        toast("请选择有效的文件", "error"); // 提示用户选择有效文件
        e.target.value = ""; // 重置文件输入框
        setFile(null);
        setSelectedFileName("");
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await request.post(
        BackendAPI.orderUploadPreview,
        null,
        formData,
      );
      if (!res.success) {
        return;
      }
      const { orders, priceList } = res.data;
      const normalized = normalizeOrderPreviewRows(
        orders,
        priceList,
        Boolean(authContext?.isCustomer()),
      );
      setPreviewData(normalized);
      setPriceList(priceList);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast("上传文件失败，请检查文件格式或者联系客服", "error");
    }
  };

  const renderPreviewTable = () => {
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>原始订单号</TableCell>
            {authContext && !authContext.isCustomer() && (
              <TableCell>客户名</TableCell>
            )}
            <TableCell>业务员</TableCell>
            <TableCell>物料名称</TableCell>
            <TableCell>物料型号</TableCell>
            <TableCell>数量</TableCell>
            <TableCell>单价</TableCell>
            {authContext && !authContext.isCustomer() && (
              <TableCell>限价表</TableCell>
            )}
            <TableCell>收件人</TableCell>
            <TableCell>收件地址</TableCell>
            <TableCell>联系电话</TableCell>
            <TableCell>创建日期</TableCell>
            <TableCell>备注</TableCell>
            <TableCell>错误信息</TableCell>
            <TableCell>操作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {previewData.map((row: OrderPreviewData, index) => {
            return (
              <TableRow
                key={index}
                style={{
                  backgroundColor:
                    row.errors.length > 0
                      ? "#ffcccc"
                      : row.warnings.length > 0
                        ? "#fffecc"
                        : "transparent",
                }}
              >
                {/* 表格行数据 */}
                <TableCell>{row.externalId}</TableCell>
                {authContext && !authContext.isCustomer() && (
                  <TableCell>{row.customerName}</TableCell>
                )}
                <TableCell>{row.salesperson}</TableCell>
                <TableCell>{row.materialName}</TableCell>
                <TableCell>{row.materialModel}</TableCell>
                <TableCell>{row.quantity}</TableCell>
                <TableCell>{row.unitPrice}</TableCell>
                {authContext && !authContext.isCustomer() && (
                  <TableCell>{row.priceListName}</TableCell>
                )}
                <TableCell>{row.billingName}</TableCell>
                <TableCell>{row.billingAddress}</TableCell>
                <TableCell>{row.billingTel}</TableCell>
                <TableCell>{row.createdAt}</TableCell>
                <TableCell>{row.note}</TableCell>
                <TableCell>
                  {[...row.errors, ...row.warnings]
                    .map((e) => formatOrderLineError(e))
                    .join(";\n")}
                </TableCell>
                <TableCell>
                  <button onClick={() => handleEdit(index)}>修改</button>
                  <button onClick={() => handleDelete(index)}>删除</button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  const handleEdit = (index) => {
    setEditData({ ...previewData[index], index });
    setIsEditModalOpen(true);
  };

  const handleDelete = (index) => {
    setPreviewData(previewData.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      setUploading(true);
      const response = await request.post(
        BackendAPI.orderUploadConfirm,
        {
          orders: previewData.map((d) => {
            const { errors, warnings, ...rest } = d;
            return {
              ...rest,
              customerId: d.customerId || d.customers?.[0]?.id,
              materialId: d.materialId || d.materials?.[0]?.id,
            };
          }),
          needKd: needKdCheck,
        },
        undefined,
        undefined,
        { suppressErrorAlert: true },
      );
      setUploading(false);
      if (!response.success) {
        toast("提交失败, 请根据下方预览修正错误后重新提交", "error");
        const raw = Array.isArray(response.data)
          ? response.data
          : response.data?.orders;
        if (raw) {
          const orders = humps.camelizeKeys(raw) as OrderPreviewData[];
          const normalized = normalizeOrderPreviewRows(
            orders,
            priceList,
            Boolean(authContext?.isCustomer()),
          );
          setPreviewData(normalized);
          previewSectionRef.current?.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        // 提示用户提交成功，跳转到订单列表页面
        toast(
          `提交成功${response.message ? `, 提示: ${response.message}` : ""}`,
          response.message ? "error" : "success",
        );
        navigator("/dashboard/orders");
      }
    } catch (error) {
      console.error("Error submitting orders:", error);
      toast("数据上传出错，请联系客服", "error");
    }
  };

  const handleSaveEdit = () => {
    const updatedPreviewData = [...previewData];
    if (editData.index === undefined) {
      return;
    }
    const index = editData.index;
    const previousData = updatedPreviewData[index];
    const newErrors: string[] = [];
    const newWarnings: string[] = [];
    const newData = { ...editData };
    if (
      !newData.materialName ||
      !newData.materialModel ||
      (previousData.errors.filter((e) =>
        ["INVALID_MATERIAL_NAME"].includes(e),
      ).length > 0 &&
        newData.materialName === previousData.materialName &&
        newData.materialModel === previousData.materialModel)
    ) {
      newErrors.push("INVALID_MATERIAL_NAME");
      if (previousData.warnings.includes("MULTIPLE_MATERIALS_MATCHED")) {
        newWarnings.push("MULTIPLE_MATERIALS_MATCHED");
      }
    }
    if (
      authContext &&
      !authContext.isCustomer() &&
      (!newData.customerName ||
        (newData.customerName === previousData.customerName &&
          previousData.errors.filter((e) =>
            ["CUSTOMER_NAME_NOT_FOUND", "INVALID_CUSTOMER_NAME"].includes(e),
          ).length > 0))
    ) {
      newErrors.push("INVALID_CUSTOMER_NAME");
      if (previousData.warnings.includes("MULTIPLE_CUSTOMERS_MATCHED")) {
        newWarnings.push("MULTIPLE_CUSTOMERS_MATCHED");
      }
    }
    if (!newData.quantity) {
      newErrors.push("QUANTITY_NOT_FOUND");
    } else if (isNaN(newData.quantity) || newData.quantity <= 0) {
      newErrors.push("QUANTITY_NOT_VALID");
    }
    if (!newData.unitPrice) {
      newErrors.push("UNIT_PRICE_NOT_FOUND");
    } else if (isNaN(newData.unitPrice) || newData.unitPrice <= 0) {
      newErrors.push("UNIT_PRICE_NOT_VALID");
    }
    if (
      !newData.billingName ||
      !newData.billingAddress ||
      !newData.billingTel
    ) {
      newErrors.push("BILLING_INFO_NOT_FOUND");
    }
    newData.errors = newErrors;
    newData.warnings = newWarnings;
    updatedPreviewData[index] = newData;
    setPreviewData(updatedPreviewData);
    setIsEditModalOpen(false);
    setCustomerNameInput("");
  };

  const hasErrors = previewData.some((row) => row.errors?.length > 0);

  const editFields = () => {
    const fields: EditField[] = [];
    if (authContext && !authContext.isCustomer()) {
      fields.push({
        label: "限价表",
        type: "select",
        options: getCustomerPriceListOptions(editData.customerCode, priceList),
        value: editData.priceListName,
        onChange: (data) => {
          setEditData({
            ...editData,
            priceListCode: data.code,
            priceListName: data.name,
          });
        },
      });
      if (authContext.isAdmin()) {
        fields.push({
          label: "客户",
          type: "select",
          options: customerOptions,
          value: editData.customerName,
          onChange: (data) => {
            setEditData({
              ...editData,
              customerId: data.id,
              customerName: data.name,
            });
          },
        });
      } else {
        fields.push({
          label: "客户匹配",
          type: "text",
          value: customerNameInput,
          onChange: (value) => {
            setCustomerNameInput(value);
          },
        });
        fields.push({
          label: "客户",
          type: "text",
          value: editData.customerName,
        });
      }
    }
    fields.push({
      label: "物料",
      type: "select",
      options: modelOptions,
      value: `${editData.materialName}-${editData.materialModel}`,
      onChange: (data) => {
        setEditData({
          ...editData,
          materialId: data.id,
          materialName: data.name,
          materialModel: data.model,
        });
      },
    });
    fields.push(
      ...[
        {
          label: "原始订单号",
          type: "text" as const,
          value: editData.externalId,
          onChange: (value) => {
            setEditData({
              ...editData,
              externalId: value,
            });
          },
        },
        {
          label: "数量",
          type: "number" as const,
          value: editData.quantity,
          onChange: (value) => {
            setEditData({
              ...editData,
              quantity: value,
            });
          },
        },
        {
          label: "单价",
          type: "number" as const,
          value: editData.unitPrice,
          onChange: (value) => {
            setEditData({
              ...editData,
              unitPrice: value,
            });
          },
        },
        {
          label: "收件人",
          type: "text" as const,
          value: editData.billingName,
          onChange: (value) => {
            setEditData({
              ...editData,
              billingName: value,
            });
          },
        },
        {
          label: "收件地址",
          type: "text" as const,
          value: editData.billingAddress,
          onChange: (value) => {
            setEditData({
              ...editData,
              billingAddress: value,
            });
          },
        },
        {
          label: "联系电话",
          type: "text" as const,
          value: editData.billingTel,
          onChange: (value) => {
            setEditData({
              ...editData,
              billingTel: value,
            });
          },
        },
        {
          label: "创建日期",
          type: "text" as const,
          value: editData.createdAt,
          onChange: (value) => {
            setEditData({
              ...editData,
              createdAt: value,
            });
          },
        },
        {
          label: "备注",
          type: "text" as const,
          value: editData.note,
          onChange: (value) => {
            setEditData({
              ...editData,
              note: value,
            });
          },
        },
      ],
    );
    return fields;
  };

  return (
    <div>
      <div className="upload-container">
        <label htmlFor="file-input" className="upload-button">
          选择文件
        </label>
        <input
          type="file"
          id="file-input"
          onChange={handleFileChange}
          accept={allowedExtensions.join(",")}
        />
        <span className="file-info">
          （支持 {allowedExtensions.join(",")} 格式）
        </span>
        {selectedFileName && (
          <span className="selected-file-info">{selectedFileName}</span>
        )}
        <button onClick={handleUpload} className="submit-button">
          上传预览
        </button>
      </div>
      <div className="info-text">
        <p>
          请使用以下模板文件进行数据上传：
          <a
            href={
              authContext && authContext.isCustomer()
                ? "/templates/订单上传模板.xlsx"
                : "/templates/订单代客户上传模板.xlsx"
            }
            download
            className="download-template-link"
          >
            下载模板
          </a>
        </p>
        <span>
          表格请不要带格式、函数、宏、公式、合并单元格，如标题行的过滤器、公式单元格等
        </span>
      </div>
      {previewData.length > 0 && (
        <div ref={previewSectionRef}>
          {!isEditModalOpen && (
            <div>
              <div>
                <span
                  style={{
                    color: "crimson",
                    fontWeight: "bold",
                  }}
                >
                  {authContext && !authContext.isCustomer() && `客户/`}
                  物料可能存在多个匹配结果，请选择正确的
                  {authContext && !authContext.isCustomer() && `客户/`}
                  物料，如果没有匹配的结果，请检查输入的
                  {authContext && !authContext.isCustomer() && `客户/`}
                  物料是否存在，或
                  {authContext && authContext.isCustomer()
                    ? `联系客服`
                    : `上报故障`}
                </span>
              </div>
              {authContext && !authContext.isCustomer() && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={needKdCheck}
                      onChange={() => setNeedKdCheck(!needKdCheck)}
                      name="needKd"
                    />
                  }
                  label="需要上传金蝶"
                />
              )}
              <button onClick={handleSubmit} disabled={hasErrors || uploading}>
                确认提交
              </button>
            </div>
          )}
          {renderPreviewTable()}
        </div>
      )}
      {isEditModalOpen && (
        <EditModal
          fields={editFields()}
          onSave={handleSaveEdit}
          onClose={() => {
            setIsEditModalOpen(false);
            setCustomerNameInput("");
          }}
        />
      )}
    </div>
  );
};

export default UploadOrder;
